import './fieldsSection.scss';

import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { XeroContact } from 'pages/requestForm/data/xero/XeroContact';
import {
    getCanCreateContact,
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
    RequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { getXeroContext } from 'pages/requestForm/selectors/xero';
import { FC, memo, useCallback, useMemo, useRef } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';

import { changeXeroSupplier, editRequestField, showXeroContactCreationPopup } from '../../../../../actions';
import { getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import { SupplierDataProvider } from '../../../dataProviders/SupplierDataProvider';
import { XeroContactDetails } from '../../../XeroContactDetails/XeroContactDetails';
import { messages } from './FieldsSection.messages';

const { xeroConstants, commonConstants } = constants;

const bem = bemFactory.block('reqf-xero-bill-fields-section');
const qa = bemFactory.qa('reqf-xero-bill-fields-section');

interface FieldsSectionProps {
    className?: string;
    request: domain.XeroCreditNotesReceivableRequest;
}

export const FieldsSection: FC<FieldsSectionProps> = memo((props) => {
    const { className, request } = props;
    const { contact, reference, date, number, brandingTheme } = request.details;

    const dispatch = useDispatch();

    const [supplierFieldPermission, dateFieldPermission, referenceFieldPermission, brandingThemeFieldPermission] =
        useSelector((state) =>
            getFieldsApprovalPermissionBySystemPurpose(state, request, [
                domain.FieldSystemPurpose.XeroSupplier,
                domain.FieldSystemPurpose.XeroDate,
                domain.FieldSystemPurpose.XeroReference,
                domain.FieldSystemPurpose.XeroBranding,
            ])
        );

    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isEditorMode = editMode === RequestEditMode.Editor;
    const isApproverMode = editMode === RequestEditMode.Approver;
    const showValidationErrors = useSelector(getShowValidationErrors);
    const isInvalidInvoiceReference = Boolean(
        (reference?.length && (!reference?.trim() || reference.length > xeroConstants.INVOICE_REFERENCE_MAX_LENGTH)) ||
            (referenceFieldPermission.required && !reference)
    );
    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));
    const initialContact = useRef(contact);

    const onCreateContact = useCallback(
        (text: string | null) => {
            dispatch(showXeroContactCreationPopup(text));

            return Promise.resolve(null);
        },
        [dispatch]
    );

    const onChangeSupplier = useCallback(
        (supplier: XeroContact | null) => {
            dispatch(changeXeroSupplier(supplier));
        },
        [dispatch]
    );

    const onChangeDocumentDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroBillDetails>('date', date));
        },
        [dispatch]
    );

    const onChangeNumber = useCallback(
        (number: string) => {
            if (commonConstants.ONLY_SPACES_REG_EXP.test(number)) {
                return;
            }

            dispatch(editRequestField<domain.XeroBillDetails>('number', number));
        },
        [dispatch]
    );

    const onChangeReference = useCallback(
        (reference: string) => {
            if (commonConstants.ONLY_SPACES_REG_EXP.test(reference)) {
                return;
            }

            dispatch(editRequestField<domain.XeroBillDetails>('reference', reference));
        },
        [dispatch]
    );

    const supplierStaticValues = useMemo(
        () => (isApproverMode && initialContact.current ? [initialContact.current] : []),
        [isApproverMode]
    );

    const xeroContext = useSelector(getXeroContext);
    const initialBrandingTheme = useRef(brandingTheme);

    const brandingThemeOptions = useMemo(
        () =>
            isApproverMode && initialBrandingTheme.current
                ? [initialBrandingTheme.current, ...xeroContext.brandingThemes]
                : xeroContext.brandingThemes,
        [isApproverMode, xeroContext.brandingThemes]
    );

    const handleChangeBrandingTheme = (value: Reference | null) => {
        dispatch(editRequestField<domain.XeroSharedDetails>('brandingTheme', value));
    };

    return (
        <div className={bem.add(className)()} data-qa={qa()}>
            <Field className={bem('field-item', 'contact')} required title={messages.contactFieldTitle}>
                <SupplierDataProvider staticValues={supplierStaticValues}>
                    <DropdownEditor
                        disabled={isEditorMode || supplierFieldPermission.disabled}
                        qa={qa('supplier-editor')}
                        value={contact}
                        singleLinePerItem={false}
                        onChange={onChangeSupplier}
                        placeholder={messages.contactFieldPlaceholder}
                        invalid={showValidationErrors && !contact}
                        canCreateItemFn={() => canCreateContact}
                        createItemDisplayTextFn={() => messages.addContactButton}
                        onCreateItem={onCreateContact}
                    />
                </SupplierDataProvider>

                <XeroContactDetails contact={contact} />
            </Field>

            <Field className={bem('field-item')} required title={messages.dateFieldTitle}>
                <DateTimeEditor
                    qa={qa('date-editor')}
                    disabled={isEditorMode || dateFieldPermission.disabled}
                    invalid={showValidationErrors && !date}
                    value={date}
                    onChange={onChangeDocumentDate}
                    placeholder={messages.dateFieldPlaceholder}
                />
            </Field>

            <Field
                className={bem('field-item')}
                required={referenceFieldPermission.required}
                title={messages.numberFieldTitle}
            >
                <TextEditor
                    qa={qa('number-editor')}
                    disabled={isEditorMode || referenceFieldPermission.disabled}
                    invalid={showValidationErrors && isInvalidInvoiceReference}
                    maxLength={constants.xeroConstants.INVOICE_REFERENCE_MAX_LENGTH}
                    value={number ?? ''}
                    onChange={onChangeNumber}
                    placeholder={messages.numberFieldPlaceholder}
                />
            </Field>

            <Field
                className={bem('field-item')}
                required={referenceFieldPermission.required}
                title={messages.referenceFieldTitle}
            >
                <TextEditor
                    qa={qa('reference-editor')}
                    disabled={isEditorMode || referenceFieldPermission.disabled}
                    invalid={showValidationErrors && isInvalidInvoiceReference}
                    maxLength={constants.xeroConstants.INVOICE_REFERENCE_MAX_LENGTH}
                    value={reference ?? ''}
                    onChange={onChangeReference}
                    placeholder={messages.referenceFieldPlaceholder}
                />
            </Field>

            <Field className={bem('field-item')} title={messages.themeFieldTitle}>
                <PlainDataProvider items={brandingThemeOptions}>
                    <DropdownEditor
                        qa={qa('branding-editor')}
                        invalid={showValidationErrors && !brandingTheme}
                        value={brandingTheme}
                        onChange={handleChangeBrandingTheme}
                        placeholder={messages.themeFieldPlaceholder}
                        buttonComponent={DropdownEditor.NoFilterButton}
                        disabled={brandingThemeFieldPermission.disabled}
                    />
                </PlainDataProvider>
            </Field>
        </div>
    );
});

FieldsSection.displayName = 'FieldsSection';
